import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Txt } from '@vst/beam';
import { ExpandMore, RadioButtonChecked, RadioButtonUnchecked } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { useScreenResolution } from '@app/hooks';
import { BuyMoreOffer, selectBuyMore, setSelectedProduct } from '@app/redux/buyMore';

import styles from './styles.module.scss';

const radioIcons = {
  checked: {
    component: RadioButtonChecked,
    name: 'RadioButtonChecked',
  },
  unchecked: {
    component: RadioButtonUnchecked,
    name: 'RadioButtonUnchecked',
  },
};

type ProductCardProps = {
  product: BuyMoreOffer;
};

const ProductCard = ({ product }: ProductCardProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('BuyMore');
  const { selectedProduct } = useSelector(selectBuyMore);

  const { amount, price, description, savingsText, id } = product;

  const [isOpen, setIsOpen] = useState(false);
  const toggleDetails = () => setIsOpen((prev) => !prev);

  const isSelected = id === selectedProduct?.id;
  const icon = isSelected ? radioIcons.checked : radioIcons.unchecked;

  const { isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;

  const selectProduct = useCallback(() => {
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label: `${AnalyticsEventNames.BuyMoreSelected} | ProductName: ${product.amount}`,
      event_category: Categories.BuyMore,
    });

    dispatch(setSelectedProduct(product));
  }, [dispatch, product]);

  return (
    <div
      data-cy={isSelected ? 'selected-product' : 'product'}
      className={isSelected ? styles['selected'] : styles['card']}
    >
      {isMobile && <SavingsText text={savingsText} className={styles['cardLabelTop']} />}
      <details
        data-cy="accordion-details"
        className={styles['accordionDetails']}
        onClick={(e) => e.preventDefault()}
        open={isOpen}
      >
        <summary className={styles['accordionSummary']}>
          <button data-cy="select-product" className={styles['productInfo']} onClick={selectProduct}>
            <div className={styles['radioIcon']}>
              <Icon data-cy={icon.name} icon={icon.component} />
            </div>
            <div className={styles['amountAndPrice']}>
              <Txt variant="heading5" as="span">
                {t('Global:dataFormat', { amount })}
              </Txt>
              <Txt variant="bodyLargeRegular" as="span">
                {t('Browse.content.product.price', { price })}
              </Txt>
            </div>
            <SavingsText text={savingsText} />
          </button>
          <button data-cy="details-button" className={styles['toggleDetails']} onClick={toggleDetails}>
            <Txt variant="bodyLargeRegular">{t('Browse.content.product.details')}</Txt>
            <Icon icon={ExpandMore} />
          </button>
        </summary>
        <Txt data-cy="description" variant="bodySmallRegular" className={styles['productDetails']} color="subtle">
          {description}
        </Txt>
      </details>
    </div>
  );
};

type SavingsTextProps = {
  text: string;
  className?: string;
};

const SavingsText = (props: SavingsTextProps): JSX.Element | null => {
  const { text, className = styles['cardLabel'] } = props;

  return text ? (
    <div data-cy="savings-text" className={className}>
      <Txt variant="tinyBold" color="inherit">
        {text}
      </Txt>
    </div>
  ) : null;
};

export default ProductCard;
